/**
 * Wave 3.46 — Customers master page.
 *
 * Mirrors the Vendors page layout (Add / Edit / Soft-delete) with two
 * differences:
 *   1. Soft-delete sets ``is_archived=true`` instead of hard-deleting so
 *      historical orders keep their snapshot customer_name.
 *   2. Surfaces a quick per-customer order summary (open / completed)
 *      sourced from /masters/customers/{id}/summary.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Archive, RefreshCw, Search, User, Coins, UserCog } from 'lucide-react';
import { formatDate } from '@/lib/format';
import { extractError } from '@/lib/errors';
import { CustomerFormDialog } from '@/components/customers/CustomerFormDialog';
import { CustomerGoldLedgerDialog } from '@/components/customers/CustomerGoldLedgerDialog';
import { CustomerGoldAllocationDialog } from '@/components/customers/CustomerGoldAllocationDialog';
import { SalesAttributionDialog } from '@/components/customers/SalesAttributionDialog';
import { useReferenceConfig } from '@/hooks/useReferenceConfig';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function Customers() {
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell
      title="Customers"
      subtitle="Master list of customers. Link new orders to a customer to track who each piece is for."
    >
      <CompanyGate>
        <CustomersContent companyId={effectiveCompanyId} />
      </CompanyGate>
    </AppShell>
  );
}

function CustomersContent({ companyId }) {
  const navigate = useNavigate();
  const { hasPermission, isSuperAdmin, isCompanyAdmin, isSalesRole, materialScope } = useAuth();
  const { enabled: refEnabled, label: refLabel, labelShort: refLabelShort } = useReferenceConfig(companyId);
  const canViewGoldLedger = hasPermission('customers.gold_ledger.view');
  // Wave 4.01 — Material-restricted buyers (e.g. Colour Stones & Pearls
  // Buyer) get a single actionable "N open" chip: the backend already
  // scopes their customer list + open count to orders carrying a pending
  // requirement in their material scope, so here we simply suppress the
  // green "done" chip which is irrelevant to their workflow.
  const isMaterialRestricted = useMemo(() => {
    if (isSuperAdmin || isCompanyAdmin || isSalesRole) return false;
    const scope = materialScope('see') || [];
    return !['gold', 'diamonds', 'color_stones', 'pearls'].every((m) => scope.includes(m));
  }, [isSuperAdmin, isCompanyAdmin, isSalesRole, materialScope]);
  // Wave 3.99ac — Sales Person / Sales Manager get a strictly READ-ONLY
  // view: the backend already scopes /masters/customers to their
  // attributed customers, so here we simply strip every mutation control
  // (Add / Edit / Archive / Restore / Sales attribution / archived toggle).
  const readOnly = !!isSalesRole;
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState({});
  // Command Palette deep-link: ``?q=<name>`` pre-fills the search box so
  // selecting a customer in the launcher lands here already filtered to them.
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get('q') || '');
  useEffect(() => {
    if (searchParams.get('q')) {
      // Clear the param (no history entry) once consumed so a later manual
      // search / refresh reflects live state rather than the stale deep-link.
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  // Add / edit dialog state.
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Archive confirmation.
  const [delTarget, setDelTarget] = useState(null);
  // Customer gold ledger popup (permission-gated).
  const [goldLedgerCustomer, setGoldLedgerCustomer] = useState(null);
  // Customer gold allocation popup (opened by clicking the customer name).
  const [allocCustomer, setAllocCustomer] = useState(null);
  // Sales attribution popup (Super Admin only).
  const [attrCustomer, setAttrCustomer] = useState(null);
  // Map of user id -> display name so attribution badges show names.
  const [salesUserMap, setSalesUserMap] = useState({});

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/masters/customers', {
        params: { company_id: companyId, include_archived: showArchived },
      });
      setList(Array.isArray(data) ? data : []);
      // Fetch per-customer summary (capped to first 100 to avoid request storms).
      const sums = {};
      await Promise.all(
        (data || []).slice(0, 100).map(async (c) => {
          try {
            const { data: s } = await api.get(`/masters/customers/${c.id}/summary`);
            sums[c.id] = s.totals;
          } catch {/* ignore */}
        }),
      );
      setSummary(sums);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load customers'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId, showArchived]);

  // Super Admin: load sales users so attribution badges can display names.
  useEffect(() => {
    if (!isSuperAdmin || !companyId) { setSalesUserMap({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/users/', { params: { company_id: companyId } });
        if (cancelled) return;
        const m = {};
        (Array.isArray(data) ? data : []).forEach((u) => {
          if (u.role === 'sales_person' || u.role === 'sales_manager') {
            m[u.id] = u.name || u.email;
          }
        });
        setSalesUserMap(m);
      } catch { /* non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, [isSuperAdmin, companyId]);

  const openAdd = () => { setEditing(null); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setOpen(true); };

  const archive = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/masters/customers/${delTarget.id}`);
      toast.success(`Archived "${delTarget.name}"`);
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(extractError(e, 'Failed to archive customer'));
    }
  };

  const restore = async (c) => {
    try {
      await api.put(`/masters/customers/${c.id}`, { is_archived: false });
      toast.success(`Restored "${c.name}"`);
      load();
    } catch (e) {
      toast.error(extractError(e, 'Failed to restore customer'));
    }
  };

  const q = (query || '').trim().toLowerCase();
  const filtered = !q
    ? list
    : list.filter((c) => {
        const hay = [c.name, c.phone, c.email, c.address].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });

  return (
    <div>
      <div className="sticky-filter-bar flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3 rounded-lg px-3" data-testid="customers-filter-bar">
        <div>
          <div className="text-base font-medium text-[var(--ink-navy)]">All Customers</div>
          <div className="text-xs text-slate-500">
            {readOnly
              ? 'Showing the customers assigned to you. Tap the blue “open” chip to jump to that customer’s open orders.'
              : 'Soft-deleted customers stay attached to historical orders but are hidden from new-order pickers.'}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, phone, email…"
              className="h-9 pl-7 w-64"
              data-testid="customers-search-input"
            />
          </div>
          {!readOnly && (
            <Button
              variant="outline"
              className="h-9 text-xs"
              onClick={() => setShowArchived((v) => !v)}
              data-testid="customers-toggle-archived"
            >
              {showArchived ? 'Hide archived' : 'Show archived'}
            </Button>
          )}
          <Button
            variant="outline"
            className="h-9 text-xs"
            onClick={load}
            disabled={loading}
            data-testid="customers-reload-button"
          >
            <RefreshCw size={13} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Reload
          </Button>
          {!readOnly && (
            <Button
              className="luxury-button h-9"
              onClick={openAdd}
              data-testid="customers-add-button"
            >
              <Plus size={14} className="mr-1.5" /> Add Customer
            </Button>
          )}
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="ledger-table overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left">Customer</th>
                <th className="text-left">Contact</th>
                <th className="text-left">Orders</th>
                {isSuperAdmin && <th className="text-left">Sales Team</th>}
                <th className="text-left">Added</th>
                <th className="text-left">Status</th>
                {!readOnly && <th className="text-right">Actions</th>}
              </tr>
            </thead>
            <tbody data-testid="customers-table-body">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5 + (isSuperAdmin ? 1 : 0) + (readOnly ? 0 : 1)} className="text-center py-10 text-slate-500" data-testid="customers-empty">
                    {list.length === 0
                      ? (readOnly
                        ? 'No customers are assigned to you yet. Ask your admin to attribute customers to you.'
                        : 'No customers yet. Click "Add Customer" to create one.')
                      : 'No matches for your search.'}
                  </td>
                </tr>
              )}
              {filtered.map((c) => {
                const tot = summary[c.id] || {};
                const archived = !!c.is_archived;
                return (
                  <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                    <td>
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 rounded-full bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] flex items-center justify-center flex-shrink-0">
                          <User size={13} />
                        </div>
                        <div className="min-w-0">
                          <button
                            type="button"
                            onClick={() => setAllocCustomer(c)}
                            className="font-medium text-[var(--ink-navy)] truncate max-w-full text-left hover:text-amber-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded transition-colors"
                            title={`View gold issued for ${c.name}'s orders`}
                            data-testid={`customer-row-name-${c.id}`}
                          >
                            {c.name}
                          </button>
                          {c.address && (
                            <div className="text-[10px] text-slate-500 truncate max-w-[280px]">
                              {c.address}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="text-xs text-slate-600">
                      {c.phone && <div className="font-mono">{c.phone}</div>}
                      {c.email && <div className="text-slate-500">{c.email}</div>}
                      {!c.phone && !c.email && <span className="text-slate-400">—</span>}
                    </td>
                    <td className="text-xs">
                      {tot.orders ? (
                        <div className="flex gap-1.5">
                          {(tot.open || 0) > 0 ? (
                            <button
                              type="button"
                              onClick={() => navigate(`/orders?customer_id=${encodeURIComponent(c.id)}&status=open`)}
                              className="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 transition-colors cursor-pointer"
                              title={`View ${tot.open} open order${tot.open === 1 ? '' : 's'} for ${c.name}`}
                              data-testid={`customer-row-open-chip-${c.id}`}
                            >
                              {tot.open} open
                            </button>
                          ) : (
                            <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200 text-[10px]">
                              0 open
                            </Badge>
                          )}
                          {tot.completed > 0 && !isMaterialRestricted && (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                              {tot.completed} done
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                      {refEnabled && tot.orders > 0 && (
                        <div className="mt-1 text-[10px] text-indigo-700" data-testid={`customer-row-unique-refs-${c.id}`}>
                          <span className="inline-flex items-center gap-0.5">
                            <span className="font-mono tabular-nums font-medium">{tot.unique_reference_count || 0}</span>
                            <span className="text-slate-500">unique {refLabel.replace(/\.$/, '')}{(tot.unique_reference_count || 0) === 1 ? '' : 's'}</span>
                          </span>
                        </div>
                      )}
                    </td>
                    {isSuperAdmin && (
                      <td className="text-xs">
                        {(c.sales_person_id || c.sales_manager_id) ? (
                          <div className="flex flex-col gap-1" data-testid={`customer-row-sales-${c.id}`}>
                            {c.sales_person_id && (
                              <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px] w-fit">
                                SP: {salesUserMap[c.sales_person_id] || '—'}
                              </Badge>
                            )}
                            {c.sales_manager_id && (
                              <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px] w-fit">
                                SM: {salesUserMap[c.sales_manager_id] || '—'}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    )}
                    <td className="text-xs text-slate-500">{c.created_at ? formatDate(c.created_at) : '—'}</td>
                    <td>
                      {archived ? (
                        <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-300 text-[10px]">
                          Archived
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                          Active
                        </Badge>
                      )}
                    </td>
                    {!readOnly && (
                    <td className="text-right">
                      <div className="flex justify-end gap-1">
                        {isSuperAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-indigo-700 hover:bg-indigo-50"
                            onClick={() => setAttrCustomer(c)}
                            data-testid={`customer-row-sales-attribution-${c.id}`}
                            title="Sales attribution"
                          >
                            <UserCog size={13} />
                          </Button>
                        )}
                        {canViewGoldLedger && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-amber-700 hover:bg-amber-50"
                            onClick={() => setGoldLedgerCustomer(c)}
                            data-testid={`customer-row-gold-ledger-${c.id}`}
                            title="Customer gold ledger"
                          >
                            <Coins size={13} />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => openEdit(c)}
                          data-testid={`customer-row-edit-${c.id}`}
                          title="Edit customer"
                        >
                          <Pencil size={13} />
                        </Button>
                        {archived ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => restore(c)}
                            data-testid={`customer-row-restore-${c.id}`}
                            title="Restore customer"
                          >
                            <RefreshCw size={13} />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-rose-600 hover:bg-rose-50"
                            onClick={() => setDelTarget(c)}
                            data-testid={`customer-row-archive-${c.id}`}
                            title="Archive customer"
                          >
                            <Archive size={13} />
                          </Button>
                        )}
                      </div>
                    </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <CustomerFormDialog
        open={open}
        onOpenChange={setOpen}
        companyId={companyId}
        customer={editing}
        onCreated={load}
        onUpdated={load}
      />

      <DeleteConfirmDialog open={!!delTarget} target={delTarget} onClose={() => setDelTarget(null)} onConfirm={archive} />

      {canViewGoldLedger && (
        <CustomerGoldLedgerDialog
          open={!!goldLedgerCustomer}
          onOpenChange={(v) => { if (!v) setGoldLedgerCustomer(null); }}
          customer={goldLedgerCustomer}
          companyId={companyId}
        />
      )}

      <CustomerGoldAllocationDialog
        open={!!allocCustomer}
        onOpenChange={(v) => { if (!v) setAllocCustomer(null); }}
        customer={allocCustomer}
        companyId={companyId}
      />

      {isSuperAdmin && (
        <SalesAttributionDialog
          open={!!attrCustomer}
          onOpenChange={(v) => { if (!v) setAttrCustomer(null); }}
          customer={attrCustomer}
          companyId={companyId}
          onSaved={load}
        />
      )}
    </div>
  );
}

function DeleteConfirmDialog({ open, target, onClose, onConfirm }) {
  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <AlertDialogContent data-testid="customer-archive-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Archive customer?</AlertDialogTitle>
          <AlertDialogDescription>
            {target
              ? `"${target.name}" will be hidden from new-order pickers. Historical orders attributed to this customer will keep their snapshot. You can restore them later via "Show archived".`
              : ''}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="customer-archive-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="customer-archive-confirm"
          >
            Archive
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
